import React, { useState } from "react";
import { useHistory, Link } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { notification } from "antd";
import { HiOutlineMail } from 'react-icons/hi';
import { useTranslation } from "react-i18next";

function Forgot() {
  const history = useHistory();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Invalid email format";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitMail = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const host = window.location.host;
      const pathName = window.location.pathname.split("/")[1];

      const response = await axiosInstance.post("pharmacy/forgot_password_check_mail", {
        email: email,
        host,
        pathname: pathName,
        module: "Pharmacy",
      });

      if (response.data.status === 1) {
        notification.success({
          message: response.data.msg || "Reset link sent to your email!",
        });
        history.push("/");
      } else {
        notification.error({
          message: response.data.msg || "Something went wrong. Please try again.",
        });
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      notification.error({
        message: "An error occurred. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-teal-50/50 flex items-center justify-center p-4">
      <div className="max-w-[500px] w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-teal-100/40 p-8 lg:p-12">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-1 tracking-tight">Recover</h1>
          <p className="text-gray-500 text-base font-medium">Request a new password</p>
        </div>

        <form onSubmit={handleSubmitMail} className="space-y-5">
          <div className="space-y-2">
            <label className="block text-base font-bold text-gray-700 ml-1 uppercase tracking-wider">Email Address</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <HiOutlineMail className="h-6 w-6 text-gray-400 group-focus-within:text-teal-600 transition-colors" />
              </div>
              <input
                type="email"
                className={`block w-full pl-12 pr-4 py-3.5 border-2 ${errors.email ? 'border-red-500 bg-red-50/10' : 'border-gray-200 bg-gray-50/50 group-hover:border-gray-300'} rounded-2xl focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 focus:bg-white transition-all duration-300 outline-none text-gray-900 text-lg font-medium shadow-sm`}
                placeholder="name@pharmacy.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors(prev => ({ ...prev, email: "" }));
                }}
              />
            </div>
            {errors.email && <p className="mt-1 text-base text-red-500 font-semibold ml-2">{errors.email}</p>}
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 px-8 rounded-2xl text-white font-bold text-lg shadow-lg shadow-teal-600/30 transform transition-all active:scale-[0.98] ${loading
                ? 'bg-gray-400 cursor-not-allowed shadow-none'
                : 'bg-teal-600 hover:bg-teal-700 hover:shadow-teal-600/50'
                }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : "Send Reset Link"}
            </button>
          </div>
        </form>

        <div className="mt-8 pt-6 border-t border-gray-100 italic text-center">
          <p className="text-gray-600 font-semibold text-base">
            Found your password? <Link to="/" className="text-teal-600 font-bold hover:text-teal-700 transition-colors hover:underline">Back to Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Forgot;
